// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
// Contained tenants sub-resource response DTOs (ADR 027 / BL-855).
import type { PreviousActionDto, ActionsLogPage } from '../../schemas/responses/actions-log';
// Central Tenant relocation + response-contract threading (ADR 027 / BL-900).
import type { TenantListView, TenantDetailView, TenantSettingsPatchDto, TenantPublishResult } from '../../schemas/responses/tenant';
// BL-1109 — session bootstrap / tenant-refresh payload.
import type { UserTenantsPayload } from '../../schemas/responses/session';
import type { TenantBuyersPremiumWriteResult } from '../../schemas/responses/buyers-premium-write';
import type { TenantCommissionWriteResult } from '../../schemas/responses/commission-write';
import type { BidderOriginPayload } from '../../schemas/responses/bidder-origin';
import type { LotCustomFieldsResponse } from '../../schemas/responses/lot-custom-fields';
import type { ClosingDefaultsResponse } from '../../schemas/responses/closing-defaults';
import type { HouseDefaultsResponse } from '../../schemas/responses/house-defaults';
import type { TenantLateFeePolicyDto } from '../../schemas/responses/late-fee-policy';
import type { TenantPaymentInstructionsDto } from '../../schemas/responses/payment-instructions';
import type { SettlementChecklistDto } from '../../schemas/responses/settlement-checklist';
import type { TenantFinancialDefaultsDto } from '../../schemas/responses/tenant-financial-defaults';
import type { TaxRateDto } from '../../schemas/responses/tax-rate';
import type {
  FinixOnboardingLinkDto,
  CardFeeDto,
  PciComplianceDto,
  TenantPaymentSettingsDto,
} from '../../schemas/responses/tenant-payment-settings';

// Platform-tier routes about ONE tenant (BL-1833 PR 4b, ADR 031 — Config
// Ownership): only a super admin passes, and they audit, archive and restore
// archived tenants too — hence `withDeleted`.
// Tenant-settings routes (BL-1833 PR 4c): the path's `:id` IS the tenant, so the
// gate loads it by primary key and another tenant's id is the one 404.
const TENANT = { from: 'record', table: 'tenant', param: 'id' } as const;
const TENANT_WITH_ARCHIVED = { ...TENANT, withDeleted: true } as const;
// The house money settings (Settings → Money): Admin and Accounting.
const MONEY_SETTINGS = 'settings.update_accounting' as const;
// Reads a Cataloger's item form needs too (custom lot fields, the tax picker).
const TAX_READ = { anyOf: ['inventory.read', 'settings.update_accounting'] } as const;
const CATALOG_READ = { anyOf: ['inventory.read', 'auction.read'] } as const;

const PLATFORM_TENANT = TENANT_WITH_ARCHIVED;
const AUDIT = { superAdmin: "a tenant's audit log is a platform support tool" } as const;
const PAYMENT_ACCOUNT = { superAdmin: "a tenant's payment-processor account is platform config" } as const;

export const tenants = {
  id: {
    actions: {
      actionId: {
        previous: defineAdminRoute({
            path: '/api/tenants/:id/actions/:actionId/previous',
            params: { id: '', actionId: '' },
            // Response DTO carrier (ADR 027 / BL-855): the previous audit row (or
            // null); `usePreviousAction` infers PreviousActionDto with no <T>.
            methods: {
              GET: { grant: AUDIT, tenant: PLATFORM_TENANT, response: responseType<PreviousActionDto>() },
            },
          }),
      },
      index: defineAdminRoute({
          path: '/api/tenants/:id/actions',
          params: { id: '' },
          // Response DTO carrier (ADR 027 / BL-979): the enriched, paginated
          // audit-row page; `useActionsLog`'s fetchOnePage infers ActionsLogPage.
          methods: {
            GET: { grant: AUDIT, tenant: PLATFORM_TENANT, response: responseType<ActionsLogPage>() },
          },
        }),
    },
    bidder_origin: defineAdminRoute({
        path: '/api/tenants/:id/bidder-origin',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-1212): the tenant's resolved
        // bidder-portal origin, so the admin Preview-as-bidder affordances can
        // build a link without duplicating the host-precedence rules client-side.
        // Read wherever a Preview-as-bidder link renders: auction pages (Admin),
        // the docked lot panel on /inventory (Cataloger), a customer page
        // (Accounting, read-only customers).
        methods: {
          GET: {
            grant: { anyOf: ['auction.read', 'inventory.read', 'customer.read_limited'] },
            tenant: TENANT,
            response: responseType<BidderOriginPayload>(),
          },
        },
      }),
    buyers_premium: defineAdminRoute({
        path: '/api/tenants/:id/buyers-premium',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-984): the house default in canonical
        // strict shape. BL-984 moved BP off `financial_defaults` (below) — this
        // is now the ONLY write path for the tenant rung of the cascade.
        methods: {
          PUT: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantBuyersPremiumWriteResult>() },
        },
      }),
    commission: defineAdminRoute({
        path: '/api/tenants/:id/commission',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-1145): the house default in
        // canonical strict shape. BL-1145 moved the commission schedule off
        // `financial_defaults` (below) — this is now the ONLY write path for
        // `tenants.default_commission_schedule`.
        methods: {
          PUT: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantCommissionWriteResult>() },
        },
      }),
    late_fees: defineAdminRoute({
        path: '/api/tenants/:id/late-fees',
        params: { id: '' },
        // BL-2095: Settings → Money → Late fees (Admin, Accounting). Its own route,
        // off `financial_defaults`, so the money fields ride a declared DTO (ADR 027,
        // rule #42) — the same move BP (BL-984) and commission (BL-1145) made.
        methods: {
          GET: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantLateFeePolicyDto>() },
          PUT: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantLateFeePolicyDto>() },
        },
      }),
    payment_instructions: defineAdminRoute({
        path: '/api/tenants/:id/payment-instructions',
        params: { id: '' },
        // BL-2578: Settings → Money → Payment instructions (Admin, Accounting). The
        // house's wire and check details on `tenants`; the DTO carries the
        // account number's last 4 only, never the number (AC 3).
        methods: {
          GET: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantPaymentInstructionsDto>() },
          PUT: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TenantPaymentInstructionsDto>() },
        },
      }),
    settlement_checklist: defineAdminRoute({
        path: '/api/tenants/:id/settlement-checklist',
        params: { id: '' },
        // BL-2500: Settings → Money → Settlement checklist (Admin, Accounting).
        // GET reads the house's live items in drag order; PUT replaces the list
        // in one transaction, refused with 409 when the quoted version is stale.
        methods: {
          GET: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<SettlementChecklistDto>() },
          PUT: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<SettlementChecklistDto>() },
        },
      }),
    financial_defaults: defineAdminRoute({
        path: '/api/tenants/:id/financial-defaults',
        params: { id: '' },
        // BL-2315 threaded both verbs onto `TenantFinancialDefaultsDto` (ADR 027).
        // GET feeds Settings → Money (Admin, Accounting) and the auction
        // bid-increment defaults page (Admin, settings.auctions).
        methods: {
          GET: {
            grant: { anyOf: ['settings.update_accounting', 'settings.update_auctions'] },
            tenant: TENANT,
            response: responseType<TenantFinancialDefaultsDto>(),
          },
          // BUG-2380: the PUT writes only the bid ladder and the off-ladder default,
          // both saved from Settings → Auctions → Bid increments, so it sits under
          // that page's grant like its sibling Auctions saves (commission and BP
          // left for their own routes, BL-1145 / BL-984).
          PUT: { grant: 'settings.update_auctions', tenant: TENANT, response: responseType<TenantFinancialDefaultsDto>() },
        },
      }),
    closing_defaults: defineAdminRoute({
        path: '/api/tenants/:id/closing-defaults',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-1208): the house closing policy a
        // new auction is stamped with at creation. GET reads, PUT replaces.
        // `useClosingDefaults` infers `ClosingDefaultsResponse` with no <T>.
        methods: {
          GET: { grant: 'settings.read', tenant: TENANT, response: responseType<ClosingDefaultsResponse>() },
          PUT: { grant: 'settings.update_auctions', tenant: TENANT, response: responseType<ClosingDefaultsResponse>() },
        },
      }),
    defaults: defineAdminRoute({
        path: '/api/tenants/:id/defaults',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-2782): the two house defaults the
        // Settings › Auctions › Defaults page saves in one request — whether a
        // new lot shows to its seller, and the contact a new auction is stamped
        // with. GET reads, PUT replaces both. `useHouseDefaults` infers the DTO.
        methods: {
          GET: { grant: 'settings.read', tenant: TENANT, response: responseType<HouseDefaultsResponse>() },
          PUT: { grant: 'settings.update_auctions', tenant: TENANT, response: responseType<HouseDefaultsResponse>() },
        },
      }),
    lot_custom_fields: defineAdminRoute({
        path: '/api/tenants/:id/lot-custom-fields',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-1231): the tenant's up-to-10 lot
        // custom-field slot configs. GET reads, PUT replaces. `useLotCustomFields`
        // infers `LotCustomFieldsResponse` with no <T>.
        methods: {
          // GET renders the slots on LotEditModal / LotCreateBody / the lot list,
          // which Catalogers use.
          GET: { grant: CATALOG_READ, tenant: TENANT, response: responseType<LotCustomFieldsResponse>() },
          PUT: { grant: 'settings.update_auctions', tenant: TENANT, response: responseType<LotCustomFieldsResponse>() },
        },
      }),
    finix: {
      onboarding: defineAdminRoute({
          path: '/api/tenants/:id/finix/onboarding',
          params: { id: '' },
          // GET retired (BL-1833 PR 4b): the Payments tab reads the onboarding
          // link off payment-settings and only POSTs here to start onboarding.
          methods: {
            POST: { grant: PAYMENT_ACCOUNT, tenant: PLATFORM_TENANT, response: responseType<FinixOnboardingLinkDto>() },
          },
        }),
      // BL-2369: re-read the house's merchant decision and approved limits from
      // the processor (the Tenant payments "Sync from Finix" button).
      merchant_sync: defineAdminRoute({
          path: '/api/tenants/:id/finix/merchant-sync',
          params: { id: '' },
          methods: {
            POST: { grant: PAYMENT_ACCOUNT, tenant: PLATFORM_TENANT, response: responseType<TenantPaymentSettingsDto>() },
          },
        }),
      onboarding_complete: defineAdminRoute({
          // External: Finix's hosted onboarding form redirects the browser here
          // as its `returnUrl` after merchant onboarding completes — see the
          // returnUrl built in `apps/admin-web/src/app/api/tenants/[id]/finix/onboarding/route.ts`.
          // No in-app typed-client caller by design (BL-623).
          path: '/api/tenants/:id/finix/onboarding-complete',
          params: { id: '' },
          // Declared, not bound (BL-1833 PR 4a): Finix redirects the browser
          // here from its hosted form, and the handler proves the round trip
          // with the state nonce it issued, not with a staff session.
          methods: {
            GET: {
              grant: { none: 'token — the Finix returnUrl state nonce authorizes it' },
              tenant: { from: 'none', reason: 'external Finix returnUrl — the state nonce names the tenant' },
            },
          },
          caller: 'external',
        }),
    },
    index: defineAdminRoute({
        path: '/api/tenants/:id',
        params: { id: '' },
        // GET + PUT return the rich single-tenant detail object (standard
        // {success,data} envelope as of BL-900 — migrated off the legacy
        // {success,tenant} shape). Both bound to the shared TenantDetailView
        // (ADR 027 / BL-900). DELETE returns no data payload.
        methods: {
          // GET: the super-admin screens (Tenants, Websites, Auction defaults)
          // and Settings → Workspace → Support, which Accounting opens
          // (settings.support). PUT's floor is `tenant.update` (Admin,
          // Accounting); its per-field check (website → super admin) stays in
          // the handler as declared residue. The reserve badge left PUT with
          // BL-2172 (it is a platform key on PATCH …/settings).
          // Both load archived tenants (`withDeleted`): a super admin views one
          // and restores it with PUT {status}; the handler refuses an archived
          // row to everyone else.
          GET: {
            grant: { anyOf: ['tenant.read', 'settings.update_support'] },
            tenant: TENANT_WITH_ARCHIVED,
            response: responseType<TenantDetailView>(),
          },
          PUT: { grant: 'tenant.update', tenant: TENANT_WITH_ARCHIVED, response: responseType<TenantDetailView>() },
          DELETE: { grant: { superAdmin: 'archiving a tenant is platform-only' }, tenant: PLATFORM_TENANT },
        },
      }),
    // BL-1152 — tenant self-scope notification templates (Settings → Workspace →
    // Notifications). Non-standard envelope (`templates`/`template` at top level,
    // matching the super-admin + auction notification routes), so no responseType.
    notification_templates: {
      index: defineAdminRoute({
          path: '/api/tenants/:id/notification-templates',
          params: { id: '' },
          methods: {
            GET: { grant: 'settings.update_support', tenant: TENANT },
            POST: { grant: 'settings.update_support', tenant: TENANT },
          },
        }),
      template: defineAdminRoute({
          path: '/api/tenants/:id/notification-templates/:templateId',
          params: { id: '', templateId: '' },
          // The service answers 404 for a template that is not this tenant's.
          methods: {
            PATCH: { grant: 'settings.update_support', tenant: TENANT },
            DELETE: { grant: 'settings.update_support', tenant: TENANT },
          },
        }),
    },
    tax_rates: {
      taxRateId: defineAdminRoute({
          path: '/api/tenants/:id/tax-rates/:taxRateId',
          params: { id: '', taxRateId: '' },
          // GET reads one rate, archived included — the drawer's self-fetch
          // (BL-1863 / ADR 029 — View Units). PATCH returns the updated rate;
          // DELETE archives and returns no data payload (BL-1766 / ADR 027 —
          // Response Contract).
          //
          // The tenant is the path's; the service scopes the rate by it, so
          // another tenant's rate id is a 404 there. Reads cover the tax picker
          // on a Cataloger's item form (BL-1833 PR 4c).
          methods: {
            GET: { grant: TAX_READ, tenant: TENANT, response: responseType<TaxRateDto>() },
            PATCH: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TaxRateDto>() },
            DELETE: { grant: MONEY_SETTINGS, tenant: TENANT },
          },
        }),
      index: defineAdminRoute({
          path: '/api/tenants/:id/tax-rates',
          params: { id: '' },
          // GET lists the tenant's rates (?includeArchived, ?state); POST
          // returns the created rate. Both bound to the shared TaxRateDto.
          methods: {
            GET: { grant: TAX_READ, tenant: TENANT, response: responseType<TaxRateDto[]>() },
            POST: { grant: MONEY_SETTINGS, tenant: TENANT, response: responseType<TaxRateDto>() },
          },
        }),
    },
    payment_settings: defineAdminRoute({
        path: '/api/tenants/:id/payment-settings',
        params: { id: '' },
        methods: {
          GET: { grant: PAYMENT_ACCOUNT, tenant: PLATFORM_TENANT, response: responseType<TenantPaymentSettingsDto>() },
          PUT: { grant: PAYMENT_ACCOUNT, tenant: PLATFORM_TENANT, response: responseType<TenantPaymentSettingsDto>() },
        },
      }),
    // BL-2375: Settings → Money → PCI compliance, the house's current Finix PCI
    // form (Admin, Accounting). A read of processor state the webhook and Sync
    // from Finix write; the house signs in the Finix Dashboard (ADR 031 —
    // Config Ownership: nothing here is house-editable). A super admin reads
    // any house for the Tenant payments row.
    pci_compliance: defineAdminRoute({
        path: '/api/tenants/:id/pci-compliance',
        params: { id: '' },
        methods: {
          GET: { grant: 'payment.read', tenant: TENANT, response: responseType<PciComplianceDto>() },
        },
      }),
    // BL-2395: Settings → Money → PCI compliance shows the house its card
    // processing fee, read-only (Admin, Accounting). A super admin sets it on
    // the Tenant payments row (ADR 031 — Config Ownership).
    card_fee: defineAdminRoute({
        path: '/api/tenants/:id/card-fee',
        params: { id: '' },
        methods: {
          GET: { grant: 'payment.read', tenant: TENANT, response: responseType<CardFeeDto>() },
        },
      }),
    publish: defineAdminRoute({
        path: '/api/tenants/:id/publish',
        params: { id: '' },
        // BL-1832 proof route, and the case where the tenant IS the resource:
        // its primary key is also its tenant column, so no intermediate lookup
        // is needed and it uses the same declaration shape as everything else.
        // One of the six verified to gate on tenant alone — it published a
        // tenant's live website settings with no permission check at all.
        methods: {
          POST: {
            grant: 'tenant.publish',
            tenant: { from: 'record', table: 'tenant', param: 'id' },
            response: responseType<TenantPublishResult>(),
          },
        },
      }),
    settings: defineAdminRoute({
        path: '/api/tenants/:id/settings',
        params: { id: '' },
        // Floor: either settings grant. The per-key checks (accounting keys →
        // settings.accounting, auction keys → settings.auctions, website keys →
        // super admin) stay in the handler as declared residue. BL-2016 bound
        // the success body to `TenantSettingsPatchDto`.
        methods: {
          PATCH: {
            grant: { anyOf: ['settings.update_accounting', 'settings.update_auctions'] },
            tenant: TENANT,
            response: responseType<TenantSettingsPatchDto>(),
          },
        },
      }),
  },
  index: defineAdminRoute({
      path: '/api/tenants',
      params: {},
      // GET lists tenants; POST creates one. Both serialize the slim
      // TenantListView (ADR 027 / BL-900) under the standard {success,data}
      // envelope — GET as { tenants: [...] }, POST as { tenant: {...} }.
      methods: {
        GET: {
          grant: { superAdmin: 'the tenant directory spans every tenant' },
          tenant: { from: 'none', reason: 'platform-wide — lists every tenant' },
          response: responseType<{ tenants: TenantListView[] }>(),
        },
        POST: {
          grant: { superAdmin: 'onboarding a tenant is platform-only' },
          tenant: { from: 'none', reason: 'platform-wide — the tenant does not exist yet' },
          response: responseType<{ tenant: TenantListView }>(),
        },
      },
    }),
  user: defineAdminRoute({
      path: '/api/tenants/user',
      params: {},
      // BL-1109: the client's tenant-list REFRESH path (the initial load is
      // server-resolved and streamed). Threaded so SessionProvider infers the
      // payload instead of asserting it.
      // Declared, not bound (BL-1833 PR 4a): the caller's own tenant list.
      methods: {
        GET: {
          grant: { none: "self — the caller's own tenant memberships" },
          tenant: { from: 'none', reason: 'self — lists every tenant the caller belongs to' },
          response: responseType<UserTenantsPayload>(),
        },
      },
    }),
};
